import React, { useState, useEffect } from "react";
import Button from "../../components/ui/Button";
import {
    Shield, Terminal, Users, Lock, Check,
    Menu, X, Copy, Server, Zap, Globe, ArrowRight,
    Package, Download, AlertTriangle, FileText,
    Container, ChevronRight, History,
    ShieldCheck, Wifi, Play, RefreshCw, Key,
    AppleIcon,
    Apple
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import CipherBackground from "../../components/ui/CipherBackground";
import SEO from "../../components/shared/SEO";
import PricingSection from "../../components/public/PricingSection";
import { useAuth } from "../../context/AuthContext";
// --- Animation Variants ---
const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.2
        }
    }
};

export default function Landing() {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const { user } = useAuth()
    const [toast, setToast] = useState("");

    // Update your copy function to handle the toast
    const copyToClipboard2 = (text, label) => {
        navigator.clipboard.writeText(text);
        setToast(`${label} command copied!`);
        setTimeout(() => setToast(""), 3000);
    };

    return (
        <>
            <SEO
                title="EnvDock | Secure Infrastructure & Secret Management"
                description="Stop sharing .env files. EnvDock provides a secure, encrypted vault to sync environment variables across teams and CI/CD pipelines."
            />
            <div className="min-h-screen bg-[#050505] text-white selection:bg-orange-500/30 selection:text-white overflow-x-hidden font-sans">

                {/* Background Texture */}
                <div className="fixed inset-0 z-0 opacity-20 pointer-events-none bg-[url('https://grainy-gradients.vercel.app/noise.svg')]" />

                {/* --- Navbar --- */}
                <nav className="fixed top-0 w-full z-50 border-b border-white/5 bg-[#050505]/80 backdrop-blur-xl">
                    {/* <div className="bg-orange-600 p-2">  <span className="w-2 h-2 rounded-full bg-white animate-pulse" /><h1 className="font-bold text-center">CURRENTLY IN DEVELOPMENT STAGE</h1></div> */}
                    <div className="container mx-auto px-6 h-16 flex items-center justify-between">
                        <div className="flex items-center gap-3 font-bold text-xl tracking-tight text-white">
                            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/20">
                                <Container size={20} className="text-white" strokeWidth={2.5} />
                            </div>
                            <span>EnvDock</span>
                        </div>

                        <div className="hidden md:flex items-center gap-8 text-sm font-medium text-gray-400">
                            <a href="#problem" className="hover:text-white transition-colors">Why?</a>
                            <a href="#features" className="hover:text-white transition-colors">Features</a>
                            <a href="#pipeline" className="hover:text-white transition-colors">How it Works</a>
                            <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
                            <a href="/docs" className="hover:text-white transition-colors">Documentation</a>
                        </div>

                        <div className="hidden md:flex items-center gap-4">
                            {user ?
                                <Link to="/dashboard/home">
                                    <Button size="sm" className="bg-orange-600 hover:bg-orange-500 text-white font-bold border-none rounded-lg px-6 shadow-xl shadow-white/5">
                                        Dashboard
                                    </Button>
                                </Link> :
                                <>
                                    <Link to="/login" className="text-sm font-bold text-gray-400 hover:text-white">Sign In</Link>
                                    <Link to="/register">
                                        <Button size="sm" className="bg-orange-600 hover:bg-orange-500 text-white font-bold border-none rounded-lg px-6 shadow-xl shadow-white/5">
                                            Get Started
                                        </Button>
                                    </Link>
                                </>}
                        </div>
                        <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="md:hidden text-white">
                            {isMobileMenuOpen ? <X /> : <Menu />}
                        </button>
                    </div>
                    {/* --- Enhanced Mobile Menu --- */}
                    <AnimatePresence>
                        {isMobileMenuOpen && (
                            <motion.div
                                initial={{ opacity: 0, y: -20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.2 }}
                                className="absolute top-0 left-0 w-full bg-[#0a0a0a] border-b border-white/10 md:hidden z-40 shadow-2xl"
                            >
                                <div className="flex flex-col p-8 gap-6 text-lg font-medium">
                                    <a href="#features" onClick={() => setIsMobileMenuOpen(false)} className="text-gray-300 hover:text-orange-500 transition-colors">Features</a>
                                    <Link to="/docs" onClick={() => setIsMobileMenuOpen(false)} className="text-gray-300 hover:text-orange-500 transition-colors">Documentation</Link>
                                    <a href="#pricing" onClick={() => setIsMobileMenuOpen(false)} className="text-gray-300 hover:text-orange-500 transition-colors">Pricing</a>
                                    <hr className="border-white/5" />
                                    {
                                        user ?
                                            <Link to="/dashboard/home" onClick={() => setIsMobileMenuOpen(false)}>
                                                <Button className="w-full bg-orange-600 text-white">Dashboard</Button>
                                            </Link>
                                            :
                                            <>
                                                <Link to="/login" onClick={() => setIsMobileMenuOpen(false)} className="text-gray-300">Sign In</Link>
                                                <Link to="/register" onClick={() => setIsMobileMenuOpen(false)}>
                                                    <Button className="w-full bg-orange-600 text-white">Get Started Free</Button>
                                                </Link>
                                            </>
                                    }

                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </nav>

                {/* --- HERO SECTION: Centered Schematic --- */}
                <HeroSection />

                {/* --- 2. THE PROBLEM (The Villain) --- */}
                <section id="problem" className="py-24 bg-[#080808] border-y border-white/5">
                    <div className="container mx-auto px-6">
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={staggerContainer}
                            className="grid md:grid-cols-2 gap-12 items-center"
                        >
                            <div>
                                <h2 className="text-5xl md:text-6xl font-black  tracking-tighter mb-6 text-white">
                                    The <span className="text-transparent bg-clip-text bg-gradient-to-r px-1 from-orange-400 to-red-600">Old Way</span> is broken
                                </h2>

                                <p className="text-gray-400 text-md md:text-lg mb-8 leading-relaxed">
                                    Managing secrets manually isn't just annoying—it's a security breach waiting to happen. How many of these sound familiar?
                                </p>

                                <ul className="space-y-4 md:space-y-6">
                                    <ProblemItem text="Copy-pasting keys into Slack or WhatsApp." delay={0.1} />
                                    <ProblemItem text="The .env.example file is 6 months out of date." delay={0.2} />
                                    <ProblemItem text="Asking 'Who has the Stripe Production Key?'" delay={0.3} />
                                    <ProblemItem text="Accidentally committing secrets to GitHub." delay={0.4} />
                                </ul>
                            </div>

                            {/* Visual representation of chaos */}
                            <div className="relative p-1 bg-gradient-to-br from-red-500/20 to-transparent rounded-3xl">
                                <div className="bg-[#111] p-8 rounded-[22px] border border-red-500/20 relative overflow-hidden">
                                    <div className="absolute top-0 right-0 p-4 opacity-10">
                                        <AlertTriangle size={120} className="text-red-500" />
                                    </div>
                                    <h3 className="text-red-500 font-bold uppercase tracking-wider mb-4 flex items-center gap-2">
                                        <AlertTriangle size={18} /> Danger Zone
                                    </h3>
                                    <div className="space-y-4 font-mono text-xs text-gray-500 bg-black/50 p-4 rounded-xl">
                                        <p><span className="text-green-400">user:</span> Hey, send me the AWS key?</p>
                                        <p><span className="text-blue-400">dev:</span> Sure, it's AKIA_29482...</p>
                                        <p><span className="text-red-500">[SYSTEM ALERT]: Secret detected in plaintext chat.</span></p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* --- 3. FEATURES (The Solution) --- */}
                <section id="features" className="py-32 container mx-auto px-6">
                    <div className="text-center max-w-3xl mx-auto mb-10 md:mb-20">
                        <h2 className="text-4xl sm:text-5xl  md:text-7xl font-black tracking-tighter text-white mb-6">
                            Everything you need for  Production-Grade <span className="text-transparent bg-clip-text bg-gradient-to-r px-2 from-orange-400 to-red-600">Secrets</span>
                        </h2>
                        <p className="text-gray-400 text-md md:text-lg">
                            Move beyond static files. EnvDock bridges the gap between local development and secure cloud infrastructure.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        {/* Card 1: Encryption */}
                        <FeatureCard
                            icon={Lock}
                            title="AES-256 Encryption"
                            desc="Your secrets are encrypted client-side before they even leave your terminal. We can't see them."
                            color="orange"
                        />

                        {/* Card 2: Environments */}
                        <FeatureCard
                            icon={Server}
                            title="Scoped Environments"
                            desc="Strict separation between Development, Staging, and Production. No more accidental prod wipes."
                            color="blue"
                        />

                        {/* Card 3: RBAC */}
                        <FeatureCard
                            icon={Users}
                            title="Granular Access"
                            desc="Junior devs see 'Development'. Senior devs see 'Production'. You control the keys."
                            color="purple"
                        />

                        {/* Card 4: Audit Logs */}
                        <FeatureCard
                            icon={FileText}
                            title="Audit Trails"
                            desc="Every 'pull' and 'push' is logged. Know exactly who accessed which variable and when."
                            color="green"
                        />

                        {/* Card 5: Versioning */}
                        <FeatureCard
                            icon={History}
                            title="Instant Rollbacks"
                            desc="Broke the build? Roll back to the previous version of your .env file in one click."
                            color="red"
                        />

                        {/* Card 6: Universal */}
                        <FeatureCard
                            icon={Globe}
                            title="Platform Agnostic"
                            desc="Works with Node, Python, Go, Rust, Docker, Kubernetes. If it uses env vars, it works."
                            color="gray"
                        />
                        <FeatureCard
                            icon={Key} // Ensure Key is imported from lucide-react
                            title="Programmable Access"
                            desc="Generate scoped API tokens to fetch secrets programmatically. Perfect for custom scripts and internal tooling."
                            color="orange"
                        />

                        {/* New Feature: Live Sync (Coming Soon) */}
                        {/* <div className="relative group"> */}
                            {/* <div className="absolute -top-3 -right-3 z-20 bg-orange-600 text-[10px] font-bold px-2 py-1 rounded text-white uppercase tracking-widest shadow-lg">
                                Soon
                            </div> */}
                            <FeatureCard
                                icon={RefreshCw}
                                title="Live Sync Engine"
                                desc="Native integrations for Vercel, Netlify, and GitHub Actions & more. Update once, sync everywhere instantly."
                                color="blue"
                            />
                        {/* </div> */}

                        {/* New Feature: Dynamic Injection */}
                        <FeatureCard
                            icon={Zap}
                            title="Runtime Injection"
                            desc="Inject secrets directly into your process memory using 'edk run'. No temporary files, no leaks to disk."
                            color="purple"
                        />
                    </div>
                </section>

                {/* --- 3.1 Injector --- */}
                <InjectorSection />


                {/* --- 4. THE PIPELINE (Visual How-To) --- */}
                <section id="pipeline" className="py-24 bg-[#0a0a0a] border-y border-white/5 relative overflow-hidden">
                    <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-5" />

                    <div className="container mx-auto px-6 relative z-10">
                        <div className="text-center mb-16">
                            <div className="inline-block px-3 py-1 bg-white/5 rounded-full text-xs font-bold text-gray-400 mb-4 border border-white/10">THE WORKFLOW</div>
                            <h2 className="text-5xl md:text-7xl font-black tracking-tighter text-white mb-6">
                                Simple as<span className="text-transparent bg-clip-text bg-gradient-to-r px-2 from-orange-400 to-red-600">Push / Pull</span>
                            </h2>
                            <p class="text-gray-400 text-md md:text-lg max-w-2xl mx-auto">No more copy-pasting keys into Slack or DMs. EnvDock keeps your team's configuration in perfect sync.</p>
                        </div>

                        <div className="grid lg:grid-cols-3 gap-8 items-center relative">

                            <div className="hidden lg:block absolute top-1/2 left-0 w-full h-1 bg-gradient-to-r from-transparent via-orange-500/20 to-transparent -translate-y-1/2 z-0" />


                            <motion.div whileHover={{ y: -5 }} className="bg-[#111] p-8 rounded-3xl border border-white/10 relative z-10 shadow-xl">
                                <div className="w-12 h-12 bg-gray-800 rounded-full flex items-center justify-center text-white font-bold text-lg mb-6 border-4 border-[#0a0a0a]">1</div>
                                <h3 className="text-xl font-bold text-white mb-2">Push Local</h3>
                                <p className="text-sm text-gray-400 mb-4">You add a new API key to your local `.env` and push it.</p>
                                <code className="block bg-black p-3 rounded-lg text-xs text-orange-400 font-mono border border-white/5">$ edk push</code>
                            </motion.div>


                            <motion.div whileHover={{ y: -5 }} className="bg-[#151515] p-8 rounded-3xl border border-orange-500/30 relative z-10 shadow-2xl shadow-orange-500/10">
                                <div className="w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center text-white font-bold text-lg mb-6 border-4 border-[#0a0a0a]">2</div>
                                <h3 className="text-xl font-bold text-white mb-2">Secure Vault</h3>
                                <p className="text-sm text-gray-400 mb-4">Secrets are encrypted and stored in the EnvDock cloud.</p>
                                <div className="flex justify-center gap-2">
                                    <Lock size={16} className="text-orange-500" />
                                    <span className="text-xs text-orange-500 font-mono">AES-256 ENCRYPTED</span>
                                </div>
                            </motion.div>


                            <motion.div whileHover={{ y: -5 }} className="bg-[#111] p-8 rounded-3xl border border-white/10 relative z-10 shadow-xl">
                                <div className="w-12 h-12 bg-gray-800 rounded-full flex items-center justify-center text-white font-bold text-lg mb-6 border-4 border-[#0a0a0a]">3</div>
                                <h3 className="text-xl font-bold text-white mb-2">Pull Anywhere</h3>
                                <p className="text-sm text-gray-400 mb-4">Your team or servers pull the latest keys instantly.</p>
                                <code className="block bg-black p-3 rounded-lg text-xs text-green-400 font-mono border border-white/5">$ edk pull -e prod</code>
                            </motion.div>
                        </div>
                    </div>
                </section>



                {/* --- 5. INSTALLATION --- */}
                {/* <section className="py-24 container mx-auto px-6 relative">
                    <AnimatePresence>
                        {toast && (
                            <motion.div
                                initial={{ opacity: 0, y: -20, x: '-50%' }}
                                animate={{ opacity: 1, y: 0, x: '-50%' }}
                                exit={{ opacity: 0, y: -20, x: '-50%' }}
                                className="fixed top-10 left-1/2 z-[100] bg-orange-600 text-white px-5 py-2.5 rounded-full font-bold shadow-2xl flex items-center gap-2 border border-white/20 text-sm whitespace-nowrap"
                            >
                                <Check size={16} /> {toast}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <div className="max-w-5xl mx-auto bg-[#111] rounded-[2rem] border border-white/10 overflow-hidden relative">
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-500 to-red-600" />

                        <div className="p-6 md:p-10 text-center">
                            <h2 className="text-3xl font-bold text-white mb-4">Install in seconds.</h2>
                            <p className="text-gray-500 mb-10 max-w-lg mx-auto text-sm leading-relaxed">
                                Choose your preferred method. Binaries include a bundled runtime so Node.js is not required.
                            </p>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                                <div
                                    className="bg-black p-5 md:p-6 rounded-2xl border border-white/10 hover:border-orange-500/50 transition-all group cursor-pointer"
                                    onClick={() => copyToClipboard2('npm i -g envdock-cli', 'NPM')}
                                >
                                    <div className="flex items-center justify-center gap-3 mb-4 text-gray-400 group-hover:text-white">
                                        <Package size={18} /> <span className="font-bold text-xs uppercase tracking-widest">NPM / Node</span>
                                    </div>
                                    <code className="text-orange-400 font-mono text-[10px] md:text-xs block bg-[#050505] p-3 rounded-lg border border-white/5 group-hover:border-orange-500/30 break-all leading-relaxed">
                                        npm install -g envdock-cli
                                    </code>
                                </div>

                                <div
                                    className="bg-black p-5 md:p-6 rounded-2xl border border-white/10 hover:border-blue-500/50 transition-all group cursor-pointer"
                                    onClick={() => copyToClipboard2('curl -sL https://envdock.cloud/api/install | bash', 'Mac/Linux')}
                                >
                                    <div className="flex items-center justify-center gap-3 mb-4 text-gray-400 group-hover:text-white">
                                        <Terminal size={18} /> <span className="font-bold text-xs uppercase tracking-widest">Mac / Linux</span>
                                    </div>
                                    <code className="text-blue-400 font-mono text-[10px] md:text-xs block bg-[#050505] p-3 rounded-lg border border-white/5 group-hover:border-blue-500/30 break-all leading-relaxed">
                                        curl -sL envdock.cloud/install | bash
                                    </code>
                                </div>

                                <div
                                    className="bg-black p-5 md:p-6 rounded-2xl border border-white/10 hover:border-purple-500/50 transition-all group cursor-pointer"
                                    onClick={() => copyToClipboard2('iwr https://envdock.cloud/api/install.ps1 -useb | iex', 'Windows')}
                                >
                                    <div className="flex items-center justify-center gap-3 mb-4 text-gray-400 group-hover:text-white">
                                        <Shield size={18} /> <span className="font-bold text-xs uppercase tracking-widest">Windows (PS)</span>
                                    </div>
                                    <code className="text-purple-400 font-mono text-[10px] md:text-xs block bg-[#050505] p-3 rounded-lg border border-white/5 group-hover:border-purple-500/30 break-all leading-relaxed">
                                        iwr envdock.cloud/install.ps1 | iex
                                    </code>
                                </div>
                            </div>
                        </div>
                    </div>
                </section> */}

                {/* --- 6. PRICING (The Coffee Cup Model) --- */}
                <PricingSection />

                {/* --- FOOTER --- */}
                <footer className="bg-black py-12 border-t border-white/10">
                    <div className="container mx-auto px-6">

                        <div className="
      flex flex-col md:flex-row 
      justify-between items-center 
      gap-8 md:gap-6
      text-center md:text-left
    ">

                            {/* Logo / Brand */}
                            <div className="flex items-center gap-2 font-bold text-white text-lg">
                                <Container className="text-orange-500" size={24} />
                                EnvDock
                            </div>

                            {/* Links */}
                            <div className="
        flex flex-wrap 
        justify-center md:justify-center 
        gap-x-6 gap-y-3
        text-gray-500 text-sm font-medium
        max-w-xl
      ">
                                <a href="/docs" className="hover:text-orange-500 transition-colors">
                                    Documentation
                                </a>

                                <a href="/privacypolicy" className="hover:text-orange-500 transition-colors">
                                    Privacy Policy
                                </a>

                                <a href="/terms" className="hover:text-orange-500 transition-colors">
                                    Terms & Conditions
                                </a>

                                <a href="/refund-policy" className="hover:text-orange-500 transition-colors">
                                    Refund Policy
                                </a>

                                <a href="/security" className="hover:text-orange-500 transition-colors">
                                    Security
                                </a>
                            </div>

                            {/* Copyright */}
                            <div className="text-gray-600 text-xs">
                                © 2026 EnvDock.
                            </div>
                        </div>
                    </div>
                </footer>

            </div>
        </>

    );
}

// Helper Sub-components

const HeroSection = () => {
    const [copied, setCopied] = useState(false);
    const [osMode, setOsMode] = useState('npm'); // 'npm' | 'unix' | 'win'

    // Dictionary for commands
    const commands = {
        npm: 'npm i -g envdock-cli',
        mac: 'brew install envdock-spec/tap/edk',
        linux: 'curl -sL https://envdock.cloud/install | bash',
        win: 'iwr https://envdock.cloud/install.ps1 -useb | iex'
    };
    const copyCommand = () => {
        navigator.clipboard.writeText(commands[osMode]);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    return (
        <section className="relative w-full h-screen min-h-[850px] flex items-center justify-center bg-[#050505] overflow-hidden">
            {/* 1. THE ANIMATED BACKGROUND */}
            <CipherBackground />
            {/* Gradient Overlay (Vignette) to make text readable */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505] z-0" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-transparent to-[#050505] z-0" />
            {/* 2. THE CONTENT (Centered & Safe) */}
            <div className="relative z-10 container mx-auto px-4 text-center">

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="max-w-4xl mx-auto"
                >


                    {/* Headline */}
                    <h1 className="text-5xl sm:text-7xl lg:text-8xl font-black tracking-tighter text-white mb-8 leading-[0.9]">
                        Sync secrets like <br /> you sync
                        <span className="text-transparent bg-clip-text bg-gradient-to-r px-2 from-orange-400 to-red-600">
                            code.
                        </span>
                    </h1>

                    {/* Subtitle */}
                    <p className="text-md md:text-2xl text-gray-400 max-w-2xl mx-auto leading-relaxed mb-6 md:mb-12">
                        Sync your <code className="bg-white/10 px-1 py-0.5 rounded text-gray-200 text-base">.env</code> files securely across your team.
                        No more Slack messages. No more leaks. Just one command.
                    </p>

                    {/* CTA Buttons */}
                    <div className="flex flex-col items-center w-full max-w-3xl mx-auto">

                        <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto mb-6 md:mb-10">
                            <Link to="/register" className="w-full sm:w-auto">
                                <button className="w-full sm:w-auto px-8 h-[42px] md:h-[66px] bg-white text-black hover:bg-gray-200 font-bold text-md md:text-lg rounded-xl transition-all hover:scale-105 active:scale-95 flex items-center justify-center gap-2 shadow-[0_0_25px_rgba(255,255,255,0.15)]">
                                    Start for free <ArrowRight size={20} />
                                </button>
                            </Link>


                            {/* <button
                                onClick={() => console.log("Open Modal Logic Here")}
                                className="w-full sm:w-auto px-8 h-[42px] md:h-[66px] bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold text-md md:text-lg rounded-xl transition-all flex items-center justify-center gap-3 group"
                            >
                                <div className="w-5 md:w-8 h-5 md:h-8 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-white/20 group-hover:scale-110 transition-all">
                                    <Play size={12} className="fill-current ml-0.5" />
                                </div>
                                <span>Watch Demo</span>
                            </button> */}
                            <a
                                href="https://youtu.be/1EQh7YrIbVk"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-full sm:w-auto px-8 h-[42px] md:h-[66px] bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold text-md md:text-lg rounded-xl transition-all flex items-center justify-center gap-3 group cursor-pointer"
                            >
                                <div className="w-5 md:w-8 h-5 md:h-8 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-white/20 group-hover:scale-110 transition-all">
                                    {/* Ensure 'Play' is imported from lucide-react */}
                                    <Play size={12} className="fill-current ml-0.5" />
                                </div>
                                <span>Watch Demo</span>
                            </a>
                        </div>

                        {/* --- ROW 2: THE TERMINAL WIDGET (Install) --- */}
                        <div className="w-full max-w-lg">
                            <div className="relative group">
                                {/* Glowing Backdrop */}
                                <div className="absolute -inset-0.5 bg-gradient-to-r from-orange-500/20 to-red-600/20 rounded-xl blur-md opacity-50 group-hover:opacity-75 transition duration-500"></div>

                                <div className="relative bg-[#080808] border border-white/10 rounded-xl overflow-hidden">

                                    {/* A. The Header / Tabs */}
                                    <div className="flex border-b border-white/5 bg-white/5">
                                        <button
                                            onClick={() => setOsMode('npm')}
                                            className={`flex-1 py-3 text-[10px] md:text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-2 transition-colors ${osMode === 'npm' ? 'bg-[#080808] text-orange-500 border-t-2 border-orange-500' : 'text-gray-500 hover:text-gray-300'}`}
                                        >
                                            <Package size={14} /> NPM
                                        </button>
                                        <button
                                            onClick={() => setOsMode('linux')}
                                            className={`flex-1 py-3 text-[10px] md:text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-2 transition-colors ${osMode === 'linux' ? 'bg-[#080808] text-blue-500 border-t-2 border-blue-500' : 'text-gray-500 hover:text-gray-300'}`}
                                        >
                                            <Terminal size={14} /> Linux
                                        </button>
                                        <button
                                            onClick={() => setOsMode('mac')}
                                            className={`flex-1 py-3 text-[10px] md:text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-2 transition-colors ${osMode === 'mac' ? 'bg-[#080808] text-green-500 border-t-2 border-green-500' : 'text-gray-500 hover:text-gray-300'}`}
                                        >
                                            <Apple size={14} /> Mac
                                        </button>
                                        <button
                                            onClick={() => setOsMode('win')}
                                            className={`flex-1 py-3 text-[10px] md:text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-2 transition-colors ${osMode === 'win' ? 'bg-[#080808] text-purple-500 border-t-2 border-purple-500' : 'text-gray-500 hover:text-gray-300'}`}
                                        >
                                            <Shield size={14} /> Windows
                                        </button>
                                    </div>

                                    {/* B. The Command Area */}
                                    <div
                                        onClick={copyCommand}
                                        className="p-5 flex items-center justify-between gap-4 cursor-pointer hover:bg-white/[0.02] transition-colors"
                                    >
                                        <div className="flex items-center gap-3 overflow-hidden">
                                            <span className="text-gray-500 select-none shrink-0">$</span>

                                            {/* Animate the text change */}
                                            <AnimatePresence mode="wait">
                                                <motion.code
                                                    key={osMode}
                                                    initial={{ opacity: 0, y: 5 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: -5 }}
                                                    className={`font-mono text-sm md:text-base whitespace-nowrap overflow-x-auto scrollbar-hide truncate ${osMode === 'npm' ? 'text-orange-400' :
                                                        osMode === 'linux' ? 'text-blue-400' : osMode === 'mac' ? 'text-green-400' : 'text-purple-400'
                                                        }`}
                                                >
                                                    {commands[osMode]}
                                                </motion.code>
                                            </AnimatePresence>
                                        </div>

                                        {/* Copy Icon Feedback */}
                                        <div className="pl-4 border-l border-white/10 shrink-0 text-gray-500">
                                            {copied ? <Check size={18} className="text-green-500" /> : <Copy size={18} />}
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>

                    {/* Trust Indicators */}
                    <div className="border-t border-white/5 pt-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-left md:text-center mt-5">

                        <div className="flex flex-col md:items-center gap-1 group">
                            <div className="flex items-center gap-1 sm:gap-2 text-gray-200 font-bold text-xs sm:text-sm">
                                <Lock size={16} className="text-orange-500" /> End-to-End Encrypted
                            </div>
                            <p className="text-xs text-gray-500 hidden md:block">Server can't read your keys</p>
                        </div>

                        <div className="flex flex-col md:items-center gap-1 group">
                            <div className="flex items-center gap-1 sm:gap-2 text-gray-200 font-bold text-xs sm:text-sm">
                                <Zap size={16} className="text-yellow-500" /> RAM-Only Injection
                            </div>
                            <p className="text-xs text-gray-500 hidden md:block">Secrets never touch the disk</p>
                        </div>

                        <div className="flex flex-col md:items-center gap-1 group">
                            <div className="flex items-center gap-1 sm:gap-2 text-gray-200 font-bold text-xs sm:text-sm">
                                <Users size={16} className="text-blue-500" /> Role-Based Access
                            </div>
                            <p className="text-xs text-gray-500 hidden md:block">Granular team permissions</p>
                        </div>

                        <div className="flex flex-col md:items-center gap-1 group">
                            <div className="flex items-center gap-1 sm:gap-2 text-gray-200 font-bold text-xs sm:text-sm">
                                <Terminal size={16} className="text-green-500" /> Stack Agnostic
                            </div>
                            <p className="text-xs text-gray-500 hidden md:block">Node, Python, Go, Docker</p>
                        </div>

                    </div>

                </motion.div>
            </div>
        </section>
    );
};


const ProblemItem = ({ text, delay }) => (
    <motion.li
        variants={{
            hidden: { opacity: 0, x: -20 },
            visible: { opacity: 1, x: 0, transition: { delay } }
        }}
        className="flex items-start gap-3"
    >
        <div className="mt-1 p-1 bg-red-500/10 rounded-full text-red-500">
            <X size={14} />
        </div>
        <span className="text-gray-300">{text}</span>
    </motion.li>
);

const FeatureCard = ({ icon: Icon, title, desc, color }) => {
    // Mapping simple color names to Tailwind classes
    const colors = {
        orange: "text-orange-500 bg-orange-500/10",
        blue: "text-blue-500 bg-blue-500/10",
        purple: "text-purple-500 bg-purple-500/10",
        green: "text-green-500 bg-green-500/10",
        red: "text-red-500 bg-red-500/10",
        gray: "text-gray-400 bg-gray-500/10",
    };

    return (
        <motion.div
            whileHover={{ y: -5 }}
            className="p-8 rounded-3xl border border-white/10 bg-[#111] hover:border-white/20 transition-all group"
        >
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-6 ${colors[color]} group-hover:scale-110 transition-transform`}>
                <Icon size={24} />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">{title}</h3>
            <p className="text-gray-400 text-sm leading-relaxed">{desc}</p>
        </motion.div>
    );
}

const InjectorSection = () => {
    return (
        <section className="py-32 bg-[#050505] relative overflow-hidden border-t border-white/5">
            <div className="container mx-auto px-6 relative z-10">
                <div className="grid lg:grid-cols-2 gap-16 items-center">

                    {/* Left: Copy */}
                    <div>
                        <div className="inline-flex items-center gap-2 px-3 py-1 bg-orange-500/10 border-l-2 border-orange-500 text-orange-500 text-[10px] font-bold uppercase tracking-widest mb-6">
                            <Zap size={12} className="fill-orange-500" /> Runtime Injection Protocol
                        </div>
                        <h2 className="text-5xl md:text-6xl font-black  tracking-tighter mb-6 text-white">
                            Works with <span className="text-transparent bg-clip-text bg-gradient-to-r px-1 from-orange-400 to-red-600">Everything</span>
                        </h2>
                        <p className="text-gray-400 text-md md:text-lg mb-8 leading-relaxed border-l border-white/10 pl-6">
                            Bypass complex integrations. Wrap your start command with <code className="text-orange-400 bg-orange-500/10 px-2 py-1 rounded-sm border border-orange-500/20">edk run</code>.
                            We inject decrypted secrets directly into process memory. <strong className="text-white">They never touch the disk.</strong>
                        </p>

                        <div className="space-y-6">
                            <InjectorItem
                                title="Zero Config"
                                desc="No webhooks. No API keys. It just works."
                            />
                            <InjectorItem
                                title="Ram-Only Security"
                                desc="Secrets exist only in volatile memory. If the server power cuts, the secrets vanish."
                            />
                            <InjectorItem
                                title="Platform Agnostic"
                                desc="Node, Python, Go, Docker, Kubernetes. If it runs in a shell, we support it."
                            />
                        </div>
                    </div>

                    {/* Right: The Standardized Terminal Demo */}
                    <div className="flex justify-center lg:justify-end">
                        <TerminalDemo />
                    </div>

                </div>
            </div>
        </section>
    );
};

const TerminalDemo = () => {
    return (
        <div className="rounded-2xl border border-white/10 bg-[#0F0F0F] font-mono text-xs md:text-sm shadow-2xl shadow-orange-500/10 w-full max-w-lg mx-auto relative group overflow-hidden">
            {/* Glossy Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-[#1a1a1a] border-b border-white/5">
                <div className="flex gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500/20 border border-red-500/50" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500/20 border border-yellow-500/50" />
                    <div className="w-3 h-3 rounded-full bg-green-500/20 border border-green-500/50" />
                </div>
                <div className="text-gray-500 text-[10px] font-bold tracking-widest uppercase">bash — 80x24</div>
            </div>

            <div className="p-6 h-fit flex flex-col justify-end pb-8 text-gray-300">
                <div className="mb-4">
                    <div className="flex items-center">
                        <span className="text-orange-500 mr-2">➜</span>
                        <span className="text-blue-400 mr-2">~</span>
                        <span className="text-gray-100">edk push production</span>
                    </div>
                    <div className="ml-4 mt-2 border-l border-white/10 pl-4 space-y-1">
                        <div className="text-gray-500 flex items-center gap-2"><Lock size={10} /> Encrypting payload (AES-256)...</div>
                        <div className="text-gray-500">{`>> Uploading to Vault...`}</div>
                        <div className="text-green-400 font-bold">✔ Secrets synced to Production.</div>
                    </div>
                </div>

                <div>
                    <div className="flex items-center">
                        <span className="text-orange-500 mr-2">➜</span>
                        <span className="text-blue-400 mr-2">~</span>
                        <span className="text-gray-100">edk run -- npm start</span>
                    </div>
                    <div className="ml-4 mt-2 text-gray-400">
                        {` > Injecting variables into process...`}<br />
                        {` > Server listening on port 3000 🚀`}
                    </div>
                    <span className="w-2 h-4 bg-orange-500 mt-2 animate-pulse block" />
                </div>
            </div>
        </div>
    );
};

const InjectorItem = ({ title, desc }) => (
    <div className="flex gap-4 items-start group">
        <div className="mt-1 w-8 h-8 bg-[#111] border border-white/10 flex items-center justify-center text-gray-500 group-hover:text-orange-500 group-hover:border-orange-500/50 transition-all shrink-0">
            <ChevronRight size={16} />
        </div>
        <div>
            <h4 className="text-white font-bold uppercase tracking-wide text-sm">{title}</h4>
            <p className="text-gray-500 text-sm leading-relaxed mt-1">{desc}</p>
        </div>
    </div>
);
